import type { PaymentMethod, SnapshotCapabilities } from '@barso/shared';

/**
 * Darf die Kasse „Karte“ anbieten (U14)? Nur wenn der zuletzt geladene Stand die Zahlart meldet: Der Basar hat die Markierung
 * eingeschaltet UND der Server nimmt `payment: 'card'` an. Ohne Meldung (älterer Server, Rückfall nach 400) gibt es nur bar.
 * „Karte“ ist eine reine Markierung für das eigene Gerät des Vereins; BARSO ist nicht angebunden (Regel 9).
 */
export function cardOffered(capabilities: Pick<SnapshotCapabilities, 'paymentMethods'>): boolean {
  return capabilities.paymentMethods?.includes('card') === true;
}

/** Klartext der Zahlart für Kasse und Verkaufsliste; fehlt sie, war es bar (Verkäufe vor U14). */
export function paymentText(payment: PaymentMethod | undefined): string {
  return payment === 'card' ? 'Karte' : 'Bar';
}

/** Pfade der Prüffehler aus einer 400-Antwort (`details: [{ path: 'sales.0.payment' }]`); leer, wenn der Server keine nennt. */
export function detailsPaths(error: unknown): string[] {
  if (typeof error !== 'object' || error === null || !('details' in error)) return [];
  const { details } = error;
  if (!Array.isArray(details)) return [];
  return (details as unknown[]).flatMap((entry) =>
    typeof entry === 'object' && entry !== null && 'path' in entry && typeof entry.path === 'string'
      ? [entry.path]
      : [],
  );
}

/**
 * Betrifft ein 400 die Zahlart? Nur dann (oder ohne Angabe von Pfaden, älteres Image) wird `card` auf bar zurückgeschrieben.
 * Nennt der 400 einen anderen Pfad (z. B. einen Code), bleibt die Zahlart unberührt und der Fehler wird geworfen.
 */
export function rejectsPayment(error: unknown): boolean {
  const paths = detailsPaths(error);
  return paths.length === 0 || paths.some((path) => /^sales\.\d+\.payment$/.test(path));
}

/**
 * Schalter der Kasse je Basar (Paket F): Darf die Kasse Preise korrigieren? Fehlt die Meldung (älterer Server, Rückfall nach
 * 400), gilt erlaubt (bisheriges Verhalten). Ein schon erfasster Verkauf geht in jedem Fall hinaus.
 */
export function priceChangeAllowed(
  capabilities: Pick<SnapshotCapabilities, 'priceChangeAllowed'>,
): boolean {
  return capabilities.priceChangeAllowed !== false;
}

/** Verkauf ohne Etikett (Freier Betrag zu unbekanntem Code, Artikel ohne Etikett) erlaubt? Fehlt die Meldung, gilt erlaubt. */
export function noLabelAllowed(
  capabilities: Pick<SnapshotCapabilities, 'noLabelAllowed'>,
): boolean {
  return capabilities.noLabelAllowed !== false;
}
